import {
  CalendarDots,
  CheckCircle,
  Star,
  UsersThree,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatNumber } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { kuniriIconWeight } from '@/lib/icons'

interface ProfileMetricsProps {
  rating: number | null
  ratingCount: number
  activitiesCreated: number
  activitiesJoined: number
  activitiesParticipated: number
}

interface MetricCardProps {
  icon: typeof CalendarDots
  value: number
  label: string
  color: string
  background: string
  border: string
}

function MetricCard({
  icon: MetricIcon,
  value,
  label,
  color,
  background,
  border,
}: MetricCardProps) {
  return (
    <div
      className="flex min-h-[104px] min-w-0 flex-col items-center justify-center rounded-[20px] border bg-white px-1 py-3 text-center shadow-card min-[400px]:min-h-[126px]"
      style={{ borderColor: border }}
    >
      <span
        className="grid size-[34px] place-items-center rounded-[12px]"
        style={{ backgroundColor: background, color }}
        aria-hidden="true"
      >
        <MetricIcon size={19} weight={kuniriIconWeight} />
      </span>
      <span className="mt-1.5 font-body text-[23px] leading-none font-bold text-deep">
        {formatNumber(value, currentLocale())}
      </span>
      <span className="mt-1.5 min-h-[26px] text-[10.5px] leading-[1.15] font-bold text-muted">
        {label}
      </span>
    </div>
  )
}

/**
 * Upstream `ProfileMetrics`: teal rating card plus the "Il tuo percorso" activity
 * counters. Shared by the own profile and the public profile so both surfaces
 * show the same D-055 data. Values come straight from the API snapshot.
 */
export function ProfileMetrics({
  rating,
  ratingCount,
  activitiesCreated,
  activitiesJoined,
  activitiesParticipated,
}: ProfileMetricsProps) {
  const { t } = useTranslation()
  const locale = currentLocale()
  const ratingLabel =
    rating == null
      ? t('profileSocial.metrics.noRatings')
      : t('profileSocial.metrics.ratingCount', { count: ratingCount })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-[23px] bg-teal px-4 py-4 text-paper">
        <span
          className="grid size-[54px] shrink-0 place-items-center rounded-[19px] bg-white/15"
          aria-hidden="true"
        >
          <Star size={29} weight="fill" className="text-salmon" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-onTeal">
            {t('profileSocial.metrics.ratingEyebrow')}
          </p>
          <p className="flex items-baseline gap-1">
            <span className="font-body text-[30px] leading-[1.1] font-bold">
              {rating == null
                ? '—'
                : formatNumber(rating, locale, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
            </span>
            {rating != null ? (
              <span className="text-[13px] font-bold text-onTeal">/ 5</span>
            ) : null}
          </p>
          <p className="text-[11px] text-onTeal">{ratingLabel}</p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-head text-[17px] font-extrabold text-deep">
            {t('profileSocial.metrics.journeyTitle')}
          </h3>
          <span className="text-[9px] font-bold text-muted/80">
            {t('profileSocial.metrics.journeyEyebrow')}
          </span>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <MetricCard
            icon={CalendarDots}
            value={activitiesCreated}
            label={t('profileSocial.metrics.created')}
            color="var(--coral)"
            background="var(--salmon-soft)"
            border="rgb(255 111 89 / 0.22)"
          />
          <MetricCard
            icon={UsersThree}
            value={activitiesJoined}
            label={t('profileSocial.metrics.joined')}
            color="var(--teal)"
            background="var(--mint)"
            border="rgb(0 100 95 / 0.22)"
          />
          <MetricCard
            icon={CheckCircle}
            value={activitiesParticipated}
            label={t('profileSocial.metrics.participated')}
            color="var(--lilla)"
            background="var(--lilla-tint)"
            border="color-mix(in srgb, var(--lilla) 22%, transparent)"
          />
        </div>
      </div>
    </div>
  )
}
